import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getRemainingSessionTime, formatRemainingTime } from './sessionUtils';
import { Tooltip } from '@mui/material';

/**
 * Session Status Indicator Component
 * Shows inactivity time and remaining time before timeout with real-time updates
 */
const SessionStatusIndicator = ({ style = {} }) => {
    const { token, isAuthenticated } = useAuth();
    const [sessionInfo, setSessionInfo] = useState({
        inactiveTime: 0,
        remainingTime: 3600000, // 1 hour default
        formattedInactiveTime: '0s',
        formattedRemainingTime: '1h 0m 0s',
        status: 'active'
    });

    useEffect(() => {
        if (!isAuthenticated || !token) {
            setSessionInfo({
                inactiveTime: 0,
                remainingTime: 0,
                formattedInactiveTime: 'Not logged in',
                formattedRemainingTime: 'N/A',
                status: 'inactive'
            });
            return;
        }

        const updateSessionInfo = () => {
            const now = Date.now();
            // Get last activity time from localStorage (synchronized with SessionTimeout)
            const lastActivity = parseInt(localStorage.getItem('lastActivityTime') || now.toString());
            const inactiveTime = now - lastActivity;
            const inactivityTimeout = 3600000; // 1 hour
            const remainingTime = Math.max(0, inactivityTimeout - inactiveTime);
            
            const formatTime = (ms) => {
                const totalSeconds = Math.floor(ms / 1000);
                const minutes = Math.floor(totalSeconds / 60);
                const seconds = totalSeconds % 60;
                
                if (minutes > 0) {
                    return `${minutes}m ${seconds}s`;
                } else {
                    return `${seconds}s`;
                }
            };
            
            let status = 'active';
            if (remainingTime <= 0) {
                status = 'expired';
            } else if (remainingTime <= 120000) { // 2 minutes
                status = 'critical';
            } else if (remainingTime <= 300000) { // 5 minutes
                status = 'warning';
            } else if (remainingTime <= 600000) { // 10 minutes
                status = 'caution';
            }

            setSessionInfo({
                inactiveTime,
                remainingTime,
                formattedInactiveTime: formatTime(inactiveTime),
                formattedRemainingTime: formatTime(remainingTime),
                status
            });
            
            // Update localStorage timestamp for next read
            localStorage.setItem('lastActivityTime', lastActivity.toString());
        };

        // Update immediately
        updateSessionInfo();

        // Update every 1 second for real-time display
        const interval = setInterval(updateSessionInfo, 1000);

        return () => clearInterval(interval);
    }, [token, isAuthenticated]);

    // Listen for activity events to update immediately
    useEffect(() => {
        if (!isAuthenticated) return;

        const handleStorageChange = (e) => {
            if (e.key === 'lastActivityTime') {
                // Immediately update when activity is detected
                const now = Date.now();
                const lastActivity = parseInt(e.newValue || now.toString());
                const inactiveTime = now - lastActivity;
                const inactivityTimeout = 3600000;
                const remainingTime = Math.max(0, inactivityTimeout - inactiveTime);
                
                const formatTime = (ms) => {
                    const totalSeconds = Math.floor(ms / 1000);
                    const minutes = Math.floor(totalSeconds / 60);
                    const seconds = totalSeconds % 60;
                    
                    if (minutes > 0) {
                        return `${minutes}m ${seconds}s`;
                    } else {
                        return `${seconds}s`;
                    }
                };
                
                let status = 'active';
                if (remainingTime <= 0) {
                    status = 'expired';
                } else if (remainingTime <= 120000) {
                    status = 'critical';
                } else if (remainingTime <= 300000) {
                    status = 'warning';
                } else if (remainingTime <= 600000) {
                    status = 'caution';
                }

                setSessionInfo({
                    inactiveTime,
                    remainingTime,
                    formattedInactiveTime: formatTime(inactiveTime),
                    formattedRemainingTime: formatTime(remainingTime),
                    status
                });
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [isAuthenticated]);

    if (!isAuthenticated) {
        return null;
    }

    const getStatusConfig = () => {
        switch (sessionInfo.status) {
            case 'critical':
                return {
                    color: '#ef4444',
                    backgroundColor: '#fef2f2',
                    borderColor: '#fecaca',
                    icon: '🚨',
                    pulse: true
                };
            case 'warning':
                return {
                    color: '#f59e0b',
                    backgroundColor: '#fef3c7',
                    borderColor: '#fde68a',
                    icon: '⚠️',
                    pulse: false
                };
            case 'caution':
                return {
                    color: '#d97706',
                    backgroundColor: '#fef3c7',
                    borderColor: '#fed7aa',
                    icon: '⏰',
                    pulse: false
                };
            case 'active':
                return {
                    color: '#10b981',
                    backgroundColor: '#f0fdf4',
                    borderColor: '#bbf7d0',
                    icon: '✅',
                    pulse: false
                };
            case 'expired':
                return {
                    color: '#dc2626',
                    backgroundColor: '#fef2f2',
                    borderColor: '#fecaca',
                    icon: '❌',
                    pulse: true
                };
            default:
                return {
                    color: '#6b7280',
                    backgroundColor: '#f9fafb',
                    borderColor: '#e5e7eb',
                    icon: '⭕',
                    pulse: false
                };
        }
    };

    const config = getStatusConfig();
    const tooltipText = `Inactive for: ${sessionInfo.formattedInactiveTime} | Timeout in: ${sessionInfo.formattedRemainingTime}`;

    return (
        <>
            <style>
                {`
                    @keyframes sessionPulse {
                        0%, 100% { opacity: 1; transform: scale(1); }
                        50% { opacity: 0.7; transform: scale(1.05); }
                    }
                    .session-pulse {
                        animation: sessionPulse 1.5s infinite;
                    }
                `}
            </style>
            <Tooltip title={tooltipText} arrow>
                <div 
                    className={config.pulse ? 'session-pulse' : ''}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        borderRadius: '8px',
                        background: config.backgroundColor,
                        border: `1px solid ${config.borderColor}`,
                        color: config.color,
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        minWidth: '140px',
                        justifyContent: 'center',
                        ...style
                    }}
                >
                    <span style={{ fontSize: '12px' }}>{config.icon}</span>
                    <span>
                        {sessionInfo.status === 'active' ? 
                            `Active: ${sessionInfo.formattedRemainingTime}` : 
                            `${sessionInfo.status.toUpperCase()}: ${sessionInfo.formattedRemainingTime}`
                        }
                    </span>
                </div>
            </Tooltip>
        </>
    );
};

export default SessionStatusIndicator;